import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { isSimulationPracticePath } from "@/lib/practice/scope";
import { FlaskConical } from "lucide-react";
import { usePracticeMode } from "@/hooks/usePracticeMode";
import { useAdminRole } from "@/hooks/useAdminRole";
import { advanceDays, getPractice, resetPractice, setPracticeOn, updatePractice } from "@/lib/practice/store";
import { SEATS, SEAT_ORDER, type Seat } from "@/lib/desk/career";
import { cn } from "@/lib/utils";

/** Owner preview controls shown only inside Simulation. */
export default function PracticeBar({ className }: { className?: string }) {
  const on = usePracticeMode();
  const qc = useQueryClient();
  if (!on) return null;
  const p = getPractice();
  const act = (fn: () => void) => { fn(); qc.invalidateQueries(); };
  const btn = "h-8 rounded-md border border-gold/60 bg-card px-3 text-xs font-semibold text-foreground hover:bg-gold/10";
  return (
    <div className={cn("flex flex-wrap items-center gap-2 rounded-xl border border-gold bg-gold/15 px-3 py-2", className)}>
      <FlaskConical className="h-4 w-4 text-gold-dark shrink-0" />
      <p className="text-xs font-semibold text-foreground">
        Practice mode: nothing is saved
        <span className="font-normal text-muted-foreground"> · {SEATS[p.seat].title} · Day {p.dayOffset + 1}</span>
      </p>
      <div className="ml-auto flex flex-wrap gap-2">
        <select
          aria-label="Practice seat"
          className={btn}
          value={p.seat}
          onChange={(e) => act(() => updatePractice((d) => ({ ...d, seat: e.target.value as Seat })))}
        >
          {SEAT_ORDER.map((s) => (
            <option key={s} value={s}>{SEATS[s].title}</option>
          ))}
        </select>
        <Button variant="outline" className={btn} onClick={() => act(() => advanceDays(1))}>Next day</Button>
        <Button variant="outline" className={btn} onClick={() => act(() => advanceDays(7))}>Next week</Button>
        <Button variant="outline" className={btn} onClick={() => act(() => { if (confirm("Start practice over as a new Researcher with $0?")) resetPractice(); })}>Reset</Button>
        <Button variant="outline" className={btn} onClick={() => act(() => setPracticeOn(false))}>Turn off</Button>
      </div>
    </div>
  );
}

/** Admin switch that turns Practice mode on. */
export function PracticeToggle({ compact }: { compact?: boolean } = {}) {
  const { data: isAdmin } = useAdminRole();
  const { pathname } = useLocation();
  const on = usePracticeMode();
  const qc = useQueryClient();
  if (!isAdmin || on || !isSimulationPracticePath(pathname)) return null;
  return (
    <Button variant="outline"
      onClick={() => { setPracticeOn(true); qc.invalidateQueries(); }}
      aria-label="Practice mode"
      title="Practice mode"
      className={compact ? "flex h-8 w-8 items-center justify-center rounded-full border border-gold/50 text-gold-dark hover:bg-gold/10" : "flex items-center gap-1 rounded-full border border-gold/60 bg-card px-3 py-1 text-xs font-semibold text-foreground hover:bg-gold/10"}
    >
      <FlaskConical className={compact ? "h-4 w-4" : "h-3.5 w-3.5"} />{!compact && " Practice mode"}
    </Button>
  );
}
